// src/data/languages.js
// Zentrale Sprach-Konfiguration: Labels + welche Sprachpaare bereits Daten haben.
// Eine neue Sprache hinzufügen = Code hier eintragen + public/data/translations/<code>.csv
// anlegen - keine weiteren Code-Änderungen nötig, Sprachpaare entstehen automatisch.
import { frenchElidesBefore } from "../utils/speechText";

export const LANGUAGE_LABELS = {
  es: "Spanisch",
  de: "Deutsch",
  en: "Englisch",
  fr: "Französisch",
  it: "Italienisch",
  nl: "Niederländisch",
  pt: "Portugiesisch",
  el: "Griechisch",
  pl: "Polnisch",
};

// Personen der Konjugationstabellen, in der Reihenfolge der Spalten in
// public/data/conjugations/<zeitform>/<code>.csv (sprachneutrale Schlüssel).
export const PERSON_SLOTS = ["1s", "2s", "3s", "1p", "2p", "3p"];

// Angezeigte Pronomen je Sprache und Person. Neue Sprache mit Verbtraining: hier eintragen
// + public/data/conjugations/present/<code>.csv anlegen.
export const PERSON_PRONOUNS = {
  es: { "1s": "yo", "2s": "tú", "3s": "él/ella", "1p": "nosotros", "2p": "vosotros", "3p": "ellos" },
  de: { "1s": "ich", "2s": "du", "3s": "er/sie/es", "1p": "wir", "2p": "ihr", "3p": "sie" },
  // "je" wird vor Vokal zu "j'" (j'ai): pronounForForm
  fr: { "1s": "je", "2s": "tu", "3s": "il/elle", "1p": "nous", "2p": "vous", "3p": "ils/elles" },
};

export function pronounFor(langCode, slot) {
  return PERSON_PRONOUNS[langCode]?.[slot] || slot;
}

/**
 * Pronomen passend zur Form: im Französischen "j'" vor Vokal oder stummem h
 * ("j'ai", "j'habite", aber "je hais", "je me souviens").
 */
export function pronounForForm(langCode, slot, form) {
  const pronoun = pronounFor(langCode, slot);
  if (langCode !== "fr" || slot !== "1s") return pronoun;
  return frenchElidesBefore(form) ? "j'" : pronoun;
}

// Sprachen, für die bereits eine public/data/translations/<code>.csv existiert.
export const AVAILABLE_LANGS = ["es", "de", "fr"];

// Quellsprachen, die in der Sprachauswahl beim Onboarding angezeigt werden (immer mit
// Zielsprache Deutsch, da die App "für Deutschsprachige" ist) - Reihenfolge wie bisher.
export const PLANNED_SOURCE_LANGS = ["es", "fr", "en", "nl", "el"];

// Alte "Quelle->Ziel"-Freitext-IDs, falls noch nicht umbenannte Profile/Fortschrittsdaten
// auftauchen (siehe Supabase-Rename-Schritt) - rein defensiv, damit nichts abstürzt.
const LEGACY_LANGUAGE_IDS = {
  "Spanish->German": { source: "es", target: "de" },
  "English->German": { source: "en", target: "de" },
  "Dutch->German": { source: "nl", target: "de" },
  "Greek->German": { source: "el", target: "de" },
};

export function languageLabel(code) {
  return LANGUAGE_LABELS[code] || code;
}

/**
 * Zerlegt eine languageId ("es-de") in { source, target }. Versteht zur Übergangszeit auch
 * die alten Freitext-IDs ("Spanish->German").
 */
export function parseLanguageId(languageId) {
  if (!languageId) return null;
  if (LEGACY_LANGUAGE_IDS[languageId]) return LEGACY_LANGUAGE_IDS[languageId];
  const [source, target] = languageId.split("-");
  if (!source || !target) return null;
  return { source, target };
}

export function buildLanguageId(source, target) {
  return `${source}-${target}`;
}

function isPairAvailable(source, target) {
  return AVAILABLE_LANGS.includes(source) && AVAILABLE_LANGS.includes(target);
}

/**
 * Optionen für den Sprachauswahl-Screen: PLANNED_SOURCE_LANGS, jeweils mit targetLang
 * gepaart, "unlocked" sobald für beide Sprachen Übersetzungsdaten vorhanden sind.
 */
export function getLanguageSelectOptions(targetLang = "de") {
  return PLANNED_SOURCE_LANGS.map((source) => ({
    id: buildLanguageId(source, targetLang),
    label: languageLabel(source),
    unlocked: isPairAvailable(source, targetLang),
  }));
}

/**
 * Anzeige-Label für eine komplette languageId, z.B. "es-de" -> "Spanisch → Deutsch".
 * Ersetzt die bisher mehrfach duplizierten labelForLanguage()-if-Ketten in den Screens.
 */
export function labelForLanguage(languageId) {
  const parsed = parseLanguageId(languageId);
  if (!parsed) return "Unbekannt";
  return `${languageLabel(parsed.source)} → ${languageLabel(parsed.target)}`;
}

export function isLanguagePairAvailable(languageId) {
  const parsed = parseLanguageId(languageId);
  if (!parsed) return false;
  return isPairAvailable(parsed.source, parsed.target);
}
